export interface Border {
  size: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  color: string;
  type: 'none' | 'solid' | 'dotted' | 'dashed';
}

export interface Shadow {
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
}

export type HTMLUnit = 'px' | 'em' | 'rem' | '%' | 'pt';

export type FontFormatPositions = 'auto' | 'left' | 'center' | 'right';

export interface LineHeight {
  value: number | string;
  unit: HTMLUnit;
}

export interface HMTLSpacing {
  top: number;
  topUnit: HTMLUnit;
  right: number;
  rightUnit: HTMLUnit;
  bottom: number;
  bottomUnit: HTMLUnit;
  left: number;
  leftUnit: HTMLUnit;
}

export interface FontFormat {
  type?: 'date' | 'number' | 'string' | 'global' | undefined;
  enabled?: boolean;
  position?: FontFormatPositions;
  size?: number;
  weight?: string;
  color?: string;
  fontFamily?: string;
  letterSpacing?: number;
  shadow?: Shadow;
  margin?: HMTLSpacing;
  padding?: HMTLSpacing;
  lineheight?: LineHeight;
  fontStyle?: string;
  contour?: any;
  presets?: Partial<FontFormat>[];
  contrastColorDark?: string;
  contrastColorLight?: string;
}

export const convertShadow = (shadow?: Shadow): string => {
  if (!shadow) return '';
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
};

/**
 * Generate CSS valid text shadow style out of TypeSpecific TextShadow
 *
 * @param {Shadow} shadow The format shadow object
 */
export const convertContour = (contour: any): string => {
  if (!contour) return '';

  const contourShadow = `-1px -1px ${contour.blur}px ${contour.color}, -1px 1px ${contour.blur}px ${contour.color}, 1px -1px ${contour.blur}px ${contour.color}, 1px 1px ${contour.blur}px ${contour.color}`;

  return contourShadow;
};

/**
 * Generate CSS valid border style out of TypeSpecific Border
 * This will return an object instead of an string like other converter functions here do
 *
 * @param {Border} border The format shadow object
 */
export const convertBorder = (border: Border): object => {
  return {
    borderStyle: border.type,
    borderColor: border.color,
    borderWidth: `${border.size.top}px ${border.size.right}px ${border.size.bottom}px ${border.size.left}px`,
  };
};

export const convertLineHeight = (lineHeight?: LineHeight | string): string => {
  if (!lineHeight) return 'initial';
  if (typeof lineHeight === 'string') {
    return lineHeight;
  }

  // if (lineHeight.value === 'initial') return 'initial';

  return `${lineHeight.value}`;
  // return `${lineHeight.value}${lineHeight.unit}`;
};

/**
 * Generate CSS valid spacing (margin/padding) value out of TypeSpecific HTMLSpacing
 */
export const convertSpacing = (spacing: HMTLSpacing, sizeFactor: number = 1): string => {
  const top = spacing.top * (spacing.topUnit !== '%' ? sizeFactor : 1) + spacing.topUnit;
  const right = spacing.right * (spacing.rightUnit !== '%' ? sizeFactor : 1) + spacing.rightUnit;
  const bottom = spacing.bottom * (spacing.bottomUnit !== '%' ? sizeFactor : 1) + spacing.bottomUnit;
  const left = spacing.left * (spacing.leftUnit !== '%' ? sizeFactor : 1) + spacing.leftUnit;

  return `${top} ${right} ${bottom} ${left}`;
};

export const getHorizontalSpacing = (spacing: HMTLSpacing, sizeFactor: number = 1) => {
  const right = spacing.right * (spacing.rightUnit !== '%' ? sizeFactor : 1) + spacing.rightUnit;
  const left = spacing.left * (spacing.leftUnit !== '%' ? sizeFactor : 1) + spacing.leftUnit;

  return { left, right };
};

export const convertFormatProperty = (property: keyof FontFormat, unit: string, fontFormat: FontFormat, sizeFactor: number = 1): object => {
  const fontStyle: any = {};
  switch (property) {
    case 'size':
      fontStyle['font-size'] = fontFormat.size! * sizeFactor + (unit || '');
      break;
    case 'lineheight':
      fontStyle['line-height'] = convertLineHeight(fontFormat.lineheight!);
      break;
    default:
      fontStyle[property] = fontFormat[property];
      break;
  }

  return fontStyle;
};

export const convertFontFormat = (fontFormat: FontFormat, prop?: keyof FontFormat, unit: string = '', sizeFactor: number = 1, background?: string) => {
  if (!fontFormat) return {};

  if (prop) {
    return convertFormatProperty(prop, unit, fontFormat, sizeFactor);
  }

  if (fontFormat.weight && typeof fontFormat.weight !== 'string') {
    console.log('!!! styleService font:', typeof fontFormat.weight, fontFormat);
  }

  const styleObj: any = {
    fontSize: `${fontFormat.size! * sizeFactor}px`,
    fontFamily: fontFormat.fontFamily,
    letterSpacing: `${fontFormat.letterSpacing || 0}px`,
    fontWeight: fontFormat.weight ? String(fontFormat.weight).replace('i', '') : 'normal',
    textAlign: fontFormat.position === 'auto' ? 'center' : fontFormat.position,
    color: fontFormat.color,
    lineHeight: convertLineHeight(fontFormat.lineheight),
    textShadow: convertShadow(fontFormat.shadow),
    margin: convertSpacing(fontFormat.margin!, sizeFactor),
    padding: convertSpacing(fontFormat.padding!, sizeFactor),
    fontStyle: fontFormat.fontStyle || 'normal',
  };

  if (background) {
    styleObj.background = background;
  }

  return styleObj;
};

const camelToKebab = (str: string) => str.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

export const styleToCSS = (styleObj: object) => {
  const styles = Object.entries(styleObj);
  const cssStyles = styles.map(([key, value]) => `${camelToKebab(key)}:${value}`);
  return cssStyles.join(';');
};
